import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, Lock, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { getBackendUrl } from "@/utils/apiBase";

const API = `${getBackendUrl()}/api`;

const REASONS = ["Vacances", "Indisponible", "Férié", "Personnel", "Autre"];

const REASON_COLORS = {
  Vacances: { bg: "#fef3c7", color: "#92400e" },
  Indisponible: { bg: "#e5e7eb", color: "#374151" },
  Férié: { bg: "#fee2e2", color: "#991b1b" },
  Personnel: { bg: "#fef9f5", color: "#b8977e" },
  Autre: { bg: "#f3f4f6", color: "#4b5563" },
};

const formatFRDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const CalendarManager = () => {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("Indisponible");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchBlocked = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/blocked-dates`);
      setBlocked(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlocked();
  }, [fetchBlocked]);

  const handleBlock = async () => {
    if (!date) {
      toast.error("Sélectionnez une date");
      return;
    }
    if (!reason) {
      toast.error("Choisissez une raison");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/blocked-dates`, {
        date,
        reason,
        note: note.trim() || null,
      });
      toast.success("Date bloquée");
      setDate("");
      setNote("");
      setReason("Indisponible");
      fetchBlocked();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur lors du blocage"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async (id) => {
    if (!window.confirm("Débloquer cette date ?")) return;
    try {
      await axios.delete(`${API}/admin/blocked-dates/${id}`);
      toast.success("Date débloquée");
      fetchBlocked();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur lors du déblocage"));
    }
  };

  const sorted = [...blocked].sort((a, b) => (a.date < b.date ? -1 : 1));

  return (
    <div className="space-y-6" data-testid="calendar-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 font-['Playfair_Display']">
            <CalendarDays className="h-6 w-6 sm:h-8 sm:w-8 text-[#b8977e]" />
            Gestion du Calendrier
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Bloquez manuellement des dates (vacances, férié, personnel) — elles seront grisées sur le calendrier public.
          </p>
        </div>
      </div>

      {/* Bloquer une date */}
      <Card className="border-[#e8d5c4] rounded-xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 font-['Playfair_Display']">
            <Plus className="h-4 w-4 text-[#b8977e]" /> Bloquer une nouvelle date
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label htmlFor="block-date" className="text-xs uppercase tracking-wider text-[#b8977e]">
                Date
              </Label>
              <Input
                id="block-date"
                data-testid="block-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
            </div>
            <div>
              <Label htmlFor="block-reason" className="text-xs uppercase tracking-wider text-[#b8977e]">
                Raison
              </Label>
              <select
                id="block-reason"
                data-testid="block-reason-select"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              >
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-1">
              <Label htmlFor="block-note" className="text-xs uppercase tracking-wider text-[#b8977e]">
                Note (optionnelle)
              </Label>
              <Input
                id="block-note"
                data-testid="block-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ex: Mariage frère"
              />
            </div>
            <Button
              onClick={handleBlock}
              disabled={submitting || !date}
              className="bg-[#b8977e] hover:bg-[#a68770] text-white w-full"
              data-testid="block-date-submit"
            >
              <Lock className="mr-2 h-4 w-4" />
              {submitting ? "Blocage..." : "Bloquer cette date"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des dates bloquées */}
      <Card className="border-[#e8d5c4] rounded-xl">
        <CardHeader>
          <CardTitle className="text-base font-['Playfair_Display']">
            Dates bloquées ({blocked.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-6">Chargement…</p>
          ) : sorted.length === 0 ? (
            <p
              data-testid="blocked-empty"
              className="text-sm text-gray-500 text-center py-6 italic"
            >
              Aucune date bloquée pour le moment.
            </p>
          ) : (
            <div className="space-y-2">
              {sorted.map((b) => {
                const colors = REASON_COLORS[b.reason] || REASON_COLORS.Autre;
                return (
                  <div
                    key={b.id}
                    data-testid={`blocked-row-${b.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-[#e8d5c4] bg-[#faf8f5] hover:bg-white transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Lock className="h-4 w-4 text-[#b8977e] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatFRDate(b.date)}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span
                            style={{
                              background: colors.bg,
                              color: colors.color,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: 50,
                            }}
                          >
                            {b.reason}
                          </span>
                          {b.note && (
                            <span className="text-xs text-gray-500 truncate">
                              — {b.note}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnblock(b.id)}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      data-testid={`unblock-${b.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarManager;
